/**
 * SAML SSO Callback Page
 *
 * Receives JWT tokens from the SAML ACS redirect and completes login.
 * URL format: /auth/saml-callback#token=xxx&refresh_token=yyy&redirect=/dashboard
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export default function SamlCallbackPage() {
  const { completeLogin } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    try {
      // Parse tokens from URL fragment (hash)
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);
      const token = params.get('token');
      const refreshToken = params.get('refresh_token');
      const redirect = params.get('redirect') || '/dashboard';

      if (!token) {
        // Check for error in query params
        const searchParams = new URLSearchParams(window.location.search);
        const errorMsg = searchParams.get('error') || searchParams.get('message');
        setError(errorMsg || 'SSO authentication failed. No token received.');
        return;
      }

      // Decode token to extract user info
      const payload = JSON.parse(atob(token.split('.')[1]));

      // Complete login using existing AuthContext
      // The completeLogin function stores tokens and sets user state.
      //
      // expiresIn is derived from the token's own `exp` claim rather than left
      // undefined. completeLogin only writes `token_expires_at` when it gets a
      // value, and AuthContext's proactive-refresh scheduler bails out with
      // `if (!expiresAtStr) return;` — so omitting it meant SSO sessions never
      // scheduled a refresh at all. They ran until the access token simply
      // expired and then took a hard 401, while password logins (which pass
      // expires_in straight from the login response) refreshed cleanly.
      const expClaim = typeof payload?.exp === 'number' ? payload.exp : null;
      const expiresIn = expClaim ? Math.max(0, expClaim - Math.floor(Date.now() / 1000)) : undefined;

      completeLogin(token, payload, refreshToken || undefined, expiresIn);

      // Clear hash from URL for security (tokens should not remain in URL)
      window.history.replaceState(null, '', window.location.pathname);

      // Redirect to intended destination
      navigate(redirect, { replace: true });
    } catch (err: any) {
      console.error('SAML callback error:', err);
      setError('Failed to complete SSO login. Please try again.');
    }
  }, [completeLogin, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-md w-full space-y-4 text-center">
          <div className="rounded-md bg-red-50 p-6">
            <h3 className="text-lg font-medium text-red-800 mb-2">SSO Login Failed</h3>
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-v2accent hover:bg-v2accent/90"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4" />
        <p className="text-gray-600">Completing sign-in...</p>
      </div>
    </div>
  );
}
